import { Facebook, Linkedin, Mail, MapPin, Phone } from 'lucide-react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

const focusRingClassName =
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950';

const footerLinkClassName = [
    'text-sm leading-6 text-slate-300 transition-colors duration-300',
    'hover:text-white',
    focusRingClassName,
].join(' ');

const footerSectionHeadingClassName =
    'text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-slate-400';

const footerContactLinkClassName = [
    'group flex items-start gap-3 text-sm leading-6 text-slate-300 transition-colors duration-300',
    'hover:text-white',
    focusRingClassName,
].join(' ');

const footerSocialLinkClassName = [
    'inline-flex h-10 w-10 items-center justify-center border border-slate-700 text-slate-300',
    'transition-colors duration-200',
    'hover:border-slate-500 hover:text-white',
    focusRingClassName,
].join(' ');

const navigationLinks = [
    { href: '/', label: 'Home' },
    { href: '/about', label: 'About Us' },
    { href: '/contact', label: 'Contact Us' },
];

const exploreLinks = [
    { href: '/products', label: 'Our Products' },
    { href: '/resources', label: 'Resources' },
    { href: '/news', label: 'News' },
    { href: '/resources/brochures', label: 'Brochures' },
    { href: '/resources/faq', label: 'FAQ' },
];

const socialLinks = [
    {
        href: 'https://www.facebook.com/trenchmateshield/',
        label: 'Facebook',
        icon: Facebook,
    },
    {
        href: 'https://nz.linkedin.com/company/trenchmate',
        label: 'LinkedIn',
        icon: Linkedin,
    },
];

function FooterContactItem({ children, href, icon: Icon }) {
    const content = (
        <>
            <Icon
                className="mt-1 h-4 w-4 flex-none text-slate-400"
                strokeWidth={1.8}
                aria-hidden="true"
            />
            <span>{children}</span>
        </>
    );

    if (href) {
        return (
            <Link href={href} className={footerContactLinkClassName}>
                {content}
            </Link>
        );
    }

    return (
        <p className="flex items-start gap-3 text-sm leading-6 text-slate-300">
            {content}
        </p>
    );
}

function FooterSeparator() {
    return (
        <span
            aria-hidden="true"
            className="inline-block h-3.5 w-px rounded-full bg-slate-700"
        />
    );
}

const Footer = () => {
    const currentYear = new Date().getFullYear();

    return (
        <footer className="shrink-0 border-t border-slate-800 bg-slate-950 text-white">
            <div className="mx-auto w-full px-4 pb-5 pt-6 sm:px-6 sm:pb-6 md:pt-8 max-w-7xl lg:px-8 lg:pt-10">
                <div className="grid gap-x-6 gap-y-8 md:grid-cols-[minmax(0,1.25fr)_minmax(0,0.78fr)_minmax(0,0.78fr)_minmax(0,0.95fr)] md:items-start lg:gap-x-10 lg:gap-y-10">
                    <section
                        className="space-y-4 md:pr-4 lg:pr-6"
                        aria-label="Company introduction"
                    >
                        <Link
                            href="/"
                            className={`inline-flex max-w-full items-center self-start ${focusRingClassName}`}
                            aria-label="Go home"
                        >
                            <AppImage
                                src="/logo/trenchmate-logo-full-white.svg"
                                alt="Trenchmate"
                                width={270}
                                height={72}
                                className="h-auto w-48 max-w-full sm:w-56 md:w-52 lg:w-64"
                                unoptimized
                            />
                        </Link>

                        <p className="max-w-[28ch] text-sm leading-6 text-slate-300 lg:max-w-[35ch] lg:leading-7">
                            We design, build, customise and deliver trench
                            shoring systems to site, helping crews across New
                            Zealand work safer and faster.
                        </p>
                    </section>

                    <nav
                        className="flex flex-col gap-3"
                        aria-label="Footer navigation"
                    >
                        <h2 className={footerSectionHeadingClassName}>
                            Navigate
                        </h2>
                        <div className="flex flex-col gap-2">
                            {navigationLinks.map((item) => (
                                <Link
                                    key={item.href}
                                    href={item.href}
                                    className={footerLinkClassName}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </div>
                    </nav>

                    <section
                        className="flex flex-col gap-3"
                        aria-label="Featured links"
                    >
                        <h2 className={footerSectionHeadingClassName}>
                            Explore
                        </h2>
                        <div className="flex flex-col gap-2">
                            {exploreLinks.map((item) => (
                                <Link
                                    key={item.href}
                                    href={item.href}
                                    className={footerLinkClassName}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </div>
                    </section>

                    <address
                        className="flex flex-col gap-4 not-italic"
                        aria-label="Contact details"
                    >
                        <h2 className={footerSectionHeadingClassName}>
                            Contact
                        </h2>
                        <div className="flex flex-col gap-3">
                            <FooterContactItem
                                href="tel:0800385274"
                                icon={Phone}
                            >
                                0800 385 274
                            </FooterContactItem>
                            <FooterContactItem
                                href="mailto:admin@trenchmate.co.nz"
                                icon={Mail}
                            >
                                admin@trenchmate.co.nz
                            </FooterContactItem>
                            <FooterContactItem icon={MapPin}>
                                149 Boundary Road, Papakura, Auckland
                            </FooterContactItem>
                        </div>

                        <div className="space-y-3 border-t border-slate-800 pt-4">
                            <p className="text-[0.68rem] font-semibold uppercase tracking-[0.24em] text-slate-400">
                                Follow Trenchmate
                            </p>
                            <div className="flex flex-wrap gap-2.5">
                                {socialLinks.map((link) => {
                                    const Icon = link.icon;

                                    return (
                                        <Link
                                            key={link.href}
                                            href={link.href}
                                            target="_blank"
                                            rel="noreferrer"
                                            className={
                                                footerSocialLinkClassName
                                            }
                                            aria-label={link.label}
                                        >
                                            <span className="sr-only">
                                                {link.label}
                                            </span>
                                            <Icon
                                                className="h-4 w-4"
                                                strokeWidth={1.8}
                                                aria-hidden="true"
                                            />
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    </address>
                </div>

                <div className="mt-8 flex flex-col gap-4 border-t border-slate-800 pt-4 text-xs text-slate-400 sm:flex-row sm:items-center sm:justify-between md:mt-10 md:pt-5">
                    <p className="flex flex-wrap items-center gap-3 leading-6">
                        <span>&copy; {currentYear} Trenchmate</span>
                        <FooterSeparator />
                        <span>All rights reserved</span>
                        <FooterSeparator />
                        <span>New Zealand wide</span>
                    </p>

                    <div className="flex flex-wrap items-center gap-3 leading-6">
                        <Link
                            href="https://techstream.co.nz"
                            target="_blank"
                            rel="noreferrer"
                            className={footerLinkClassName}
                        >
                            Designed and powered by Techstream
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
